import { useRef } from 'react'
import { useMotionValue, useTransform, type PanInfo } from 'motion/react'

interface SwipeToDismissOptions {
  onClose: () => void
  /** Pixels to drag before triggering close (default: 100) */
  threshold?: number
  /** Whether swiping is currently enabled (default: true) */
  enabled?: boolean
}

/**
 * Provides drag-to-dismiss behavior for bottom sheets.
 * Returns motion props to spread onto the sheet's motion.div.
 */
export function useSwipeToDismiss({ onClose, threshold = 100, enabled = true }: SwipeToDismissOptions) {
  const y = useMotionValue(0)
  const backdropOpacity = useTransform(y, [0, 300], [1, 0.2])
  const closingRef = useRef(false)

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (!enabled || closingRef.current) return
    if (info.offset.y > threshold || info.velocity.y > 500) {
      closingRef.current = true
      onClose()
      // Reset after animation completes
      setTimeout(() => { closingRef.current = false }, 400)
    }
  }

  const sheetProps = enabled ? {
    drag: 'y' as const,
    dragConstraints: { top: 0, bottom: 0 },
    dragElastic: { top: 0, bottom: 0.6 },
    onDragEnd: handleDragEnd,
    style: { y },
  } : {}

  return { sheetProps, backdropOpacity, y }
}
